import FormulaParserPkg from 'fast-formula-parser'
import * as FJS from '@formulajs/formulajs'

const FormulaParser: any = (FormulaParserPkg as any)?.default ?? FormulaParserPkg

/**
 * fast-formula-parser 는 파서·참조 해석은 좋지만 함수 본문이 **빈 스텁**인 게 32개 있고
 * (MAX, MIN, MEDIAN, SMALL, LARGE …) SUMIFS·COUNTIFS·SUBSTITUTE·TEXTJOIN·재무함수처럼
 * 아예 없는 것도 많다. 빈 스텁은 undefined 를 반환해 NOT_IMPLEMENTED 로 떨어진다.
 *
 * 그래서 formulajs(MIT, ~500함수)로 그 구멍만 메운다.
 * - 정상 구현된 내장 함수는 건드리지 않는다 (참조/컨텍스트 처리가 더 정확하다).
 * - 빈 스텁이거나 아예 없는 이름만 formulajs 구현으로 등록한다.
 */

/** 파서가 첫 인자로 context 를 넘겨주는 함수들 (hooks.js 하드코딩 목록) */
const CONTEXT_FUNCS = new Set([
  'ROW',
  'ROWS',
  'COLUMN',
  'COLUMNS',
  'SUMIF',
  'INDEX',
  'AVERAGEIF',
  'IF',
  'OFFSET',
  'INDIRECT',
  'CHOOSE',
  'WEBSERVICE',
])

/** 참조/컨텍스트 의미가 있어서 formulajs 로 대체하면 안 되는 것들 */
const NEVER_OVERRIDE = new Set([...CONTEXT_FUNCS, 'NA', 'TRUE', 'FALSE'])

function isEmptyStub(fn: unknown): boolean {
  if (typeof fn !== 'function') return false
  const src = String(fn)
  const body = src.slice(src.indexOf('{') + 1, src.lastIndexOf('}'))
  return body.trim() === ''
}

/** fast-formula-parser 가 넘겨주는 인자 래퍼를 평범한 값/배열로 편다. */
function unwrap(arg: any): any {
  if (arg === null || arg === undefined) return null
  if (typeof arg === 'object' && !Array.isArray(arg) && 'value' in arg) {
    if (arg.omitted) return undefined
    return unwrap(arg.value)
  }
  if (Array.isArray(arg)) return arg.map(unwrap)
  if (arg instanceof Date) return arg
  return arg
}

/** formulajs 는 오류를 Error 인스턴스(message='#VALUE!')로 돌려준다. */
function normalizeResult(v: any): any {
  if (v instanceof Error) return v.message && /^#/.test(v.message) ? v.message : '#VALUE!'
  if (typeof v === 'number' && !Number.isFinite(v)) return '#NUM!'
  if (v instanceof Date) {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`
  }
  if (Array.isArray(v)) {
    // 동적배열은 아직 지원하지 않는다 — 좌상단 값만 취한다
    const flat = v.flat ? v.flat(2) : v
    return flat.length ? normalizeResult(flat[0]) : null
  }
  return v === undefined ? null : v
}

function wrap(name: string, impl: (...a: any[]) => any) {
  const needsContext = CONTEXT_FUNCS.has(name)
  return (...args: any[]) => {
    const real = needsContext ? args.slice(1) : args
    try {
      const out = impl(...real.map(unwrap))
      return normalizeResult(out)
    } catch (e: any) {
      const msg = String(e?.message ?? '')
      return /^#/.test(msg) ? msg : '#VALUE!'
    }
  }
}

/**
 * formulajs 에서 STDEV·VAR·RANK·MODE 등은 함수가 아니라
 * { S, P, EQ, AVG, SNGL, MULT } 를 담은 네임스페이스 객체다.
 * 맨이름(STDEV)에 쓸 기본 변형을 Excel 관례대로 고른다.
 */
const NS_DEFAULT_KEY = ['S', 'EQ', 'SNGL', 'INC', 'DIST', 'P']

function pickDefault(ns: Record<string, unknown>): ((...a: any[]) => any) | null {
  for (const k of NS_DEFAULT_KEY) if (typeof ns[k] === 'function') return ns[k] as any
  const first = Object.values(ns).find((v) => typeof v === 'function')
  return (first as any) ?? null
}

/** formulajs 에 없는데 실무에서 꼭 쓰는 것들 */
function extraFunctions(): Record<string, (...a: any[]) => any> {
  const flat = (v: any): any[] => (Array.isArray(v) ? v.flat(2) : [v])
  const eq = (a: any, b: any) =>
    typeof a === 'string' && typeof b === 'string' ? a.toLowerCase() === b.toLowerCase() : a === b

  return {
    // XLOOKUP(찾을값, 찾을범위, 반환범위, [없을때], [일치모드])
    XLOOKUP: (needle: any, lookup: any, result: any, ifNotFound: any = '#N/A', mode: any = 0) => {
      const L = flat(lookup)
      const R = flat(result)
      let idx = -1
      if (Number(mode) === 0) idx = L.findIndex((v) => eq(v, needle))
      else if (Number(mode) === -1) {
        for (let i = 0; i < L.length; i++) if (L[i] !== null && L[i] <= needle) idx = i
      } else if (Number(mode) === 1) {
        for (let i = L.length - 1; i >= 0; i--) if (L[i] !== null && L[i] >= needle) idx = i
      } else idx = L.findIndex((v) => eq(v, needle))
      if (idx < 0 || idx >= R.length) return ifNotFound
      return R[idx]
    },
  }
}

/** 실제로 등록할 보강 함수 맵과, 무엇을 왜 채웠는지에 대한 리포트 */
export function buildCustomFunctions(): {
  functions: Record<string, (...a: any[]) => any>
  filledStubs: string[]
  added: string[]
} {
  const builtin: Record<string, unknown> = new FormulaParser().functions ?? {}
  const functions: Record<string, (...a: any[]) => any> = {}
  const filledStubs: string[] = []
  const added: string[] = []

  const register = (name: string, impl: (...a: any[]) => any) => {
    if (NEVER_OVERRIDE.has(name)) return
    const existing = builtin[name]
    if (existing === undefined) {
      functions[name] = wrap(name, impl)
      added.push(name)
    } else if (isEmptyStub(existing)) {
      functions[name] = wrap(name, impl)
      filledStubs.push(name)
    }
  }

  for (const [name, impl] of Object.entries(FJS as Record<string, unknown>)) {
    if (!/^[A-Z][A-Z0-9_.]*$/.test(name)) continue

    if (typeof impl === 'function') {
      register(name, impl as any)
      continue
    }
    // STDEV / VAR / RANK / MODE 같은 네임스페이스 객체
    if (impl && typeof impl === 'object') {
      const ns = impl as Record<string, unknown>
      for (const [key, sub] of Object.entries(ns)) {
        if (typeof sub === 'function' && /^[A-Z][A-Z0-9_]*$/.test(key)) register(`${name}.${key}`, sub as any)
      }
      const def = pickDefault(ns)
      if (def) register(name, def)
    }
  }

  for (const [name, impl] of Object.entries(extraFunctions())) register(name, impl)

  return { functions, filledStubs: filledStubs.sort(), added: added.sort() }
}

export const CUSTOM = buildCustomFunctions()
